import React, { useState, useEffect } from 'react';
import './AgencyDashboard.css';
import api from '../services/api';
import * as XLSX from 'xlsx';

const AgencyDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({ commission: false, devices: false });
  const [stats, setStats] = useState({
    totalSchools: 0,
    activeStudents: 0,
    activeDevices: 0,
    totalRevenue: 0,
    commissionEarned: 0,
    commissionType: 'percentage'
  });
  const [schools, setSchools] = useState([]);
  const [reports, setReports] = useState([]);
  const [devices, setDevices] = useState([]);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [showEditSchool, setShowEditSchool] = useState(false);
  const [editSchoolLoading, setEditSchoolLoading] = useState(false);
  const [editSchoolUsers, setEditSchoolUsers] = useState([]);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [resettingUserPassword, setResettingUserPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showSingleAssign, setShowSingleAssign] = useState(false);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: ''
  });
  const [newSchool, setNewSchool] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    commission_type: 'percentage',
    sales_commission: process.env.REACT_APP_SCHOOL_DEFAULT_COMMISSION || 10
  });
  const [editSchoolForm, setEditSchoolForm] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    commission_type: 'percentage',
    sales_commission: '',
    status: 'active'
  });
  const [singleAssignForm, setSingleAssignForm] = useState({
    device_id: '',
    school_id: ''
  });
  const [singleAssignDeviceLocked, setSingleAssignDeviceLocked] = useState(false);
  const [bulkAssignFile, setBulkAssignFile] = useState(null);
  const [bulkAssignSchoolId, setBulkAssignSchoolId] = useState('');
  const [bulkAssignResult, setBulkAssignResult] = useState(null);
  const [commissionSubTab, setCommissionSubTab] = useState('summary');
  const [commissionSummary, setCommissionSummary] = useState({
    total_accrued: 0,
    total_pending: 0,
    total_paid: 0,
    transaction_count: 0,
    available_balance: 0,
    total_withdrawn: 0,
    commission_rate: 0,
    commission_type: 'percentage'
  });

  const formatCommissionRate = (summary, fallbackStats) => {
    const type = summary?.commission_type ?? fallbackStats?.commissionType ?? 'percentage';
    const first = Number(
      summary?.first_purchase_rate ?? summary?.commission_rate ?? fallbackStats?.commissionRate ?? 0
    );
    const renewal = Number(
      summary?.renewal_rate ?? summary?.commission_rate ?? fallbackStats?.commissionRate ?? 0
    );
    const fmt = (rate) => (type === 'fixed' ? `₹${rate}` : `${rate}%`);
    return `First ${fmt(first)} / Renewal ${fmt(renewal)}`;
  };
  const [commissionReports, setCommissionReports] = useState([]);
  const [schoolWiseReports, setSchoolWiseReports] = useState([]);
  const [commissionTransactions, setCommissionTransactions] = useState([]);
  const [withdrawalForm, setWithdrawalForm] = useState({
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    amount: '',
    notes: ''
  });
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [withdrawalMessage, setWithdrawalMessage] = useState('');
  const [commissionFilters, setCommissionFilters] = useState({
    start_date: '',
    end_date: '',
    school_id: '',
    standard: '',
    division: ''
  });
  const [salesReports, setSalesReports] = useState([]);
  const [salesSummary, setSalesSummary] = useState({
    total_devices: 0,
    sold_to_school: 0,
    inventory: 0,
    assigned_to_student: 0
  });
  const [salesFilters, setSalesFilters] = useState({
    school_id: '',
    assignment_status: ''
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const pageTitles = {
    overview: { title: 'Overview', subtitle: 'Agency performance at a glance' },
    schools: { title: 'Schools', subtitle: 'Manage schools under this agency' },
    devices: { title: 'Devices', subtitle: 'Inventory and school assignment' },
    commission: { title: 'Commission', subtitle: 'Earnings, withdrawals and reports' },
    reports: { title: 'Sales Reports', subtitle: 'Device sales and assignment status' }
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  const goToTab = (tab, options = {}) => {
    const { submenu, expand } = options;
    if (tab === 'commission') {
      if (submenu) setCommissionSubTab(submenu);
      setExpandedMenus((p) => ({ ...p, commission: true }));
    }
    if (expand) {
      setExpandedMenus((p) => ({ ...p, [expand]: true }));
    }
    setActiveTab(tab);
    closeMobileNav();
  };

  const toggleMenu = (key) => {
    setExpandedMenus((p) => ({ ...p, [key]: !p[key] }));
  };

  const openCommissionMenu = () => {
    setExpandedMenus((p) => {
      const next = !p.commission;
      if (next) setActiveTab('commission');
      return { ...p, commission: next };
    });
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      // Fetch stats
      const statsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const statsData = await statsResponse.json();
      let loadedStats = null;
      if (statsResponse.ok) {
        loadedStats = statsData;
        setStats(statsData);
      }

      // Fetch schools
      const schoolsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/schools`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const schoolsData = await schoolsResponse.json();
      if (schoolsResponse.ok) {
        setSchools(schoolsData);
      }

      // Fetch reports
      const reportsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/reports`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const reportsData = await reportsResponse.json();
      if (reportsResponse.ok) {
        setReports(reportsData);
      }

      // Fetch devices
      const devicesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/devices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const devicesData = await devicesResponse.json();
      if (devicesResponse.ok) {
        setDevices(devicesData);
      }

      await loadCommissionData('detail', loadedStats);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const loadCommissionData = async (groupBy = 'detail', statsFallback = null, filtersOverride = null) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const filters = filtersOverride || commissionFilters;
      const params = new URLSearchParams();
      params.append('group_by', groupBy);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.school_id) params.append('school_id', filters.school_id);
      if (filters.standard) params.append('standard', filters.standard);
      if (filters.division) params.append('division', filters.division);

      const rateFallback = statsFallback || stats;

      const summaryResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/commissions/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let summary = {};
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        summary = summaryData.summary || {};
      }

      if ((summary.commission_rate == null) && rateFallback?.commissionRate) {
        summary.commission_rate = rateFallback.commissionRate;
        summary.commission_type = rateFallback.commissionType || 'percentage';
      }

      if (summary.commission_rate == null) {
        const settingsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/commission-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (settingsResponse.ok) {
          const settings = await settingsResponse.json();
          summary = {
            ...summary,
            commission_rate: settings.commission_rate ?? 0,
            commission_type: settings.commission_type || 'percentage',
            first_purchase_rate: settings.first_purchase_rate,
            renewal_rate: settings.renewal_rate
          };
        }
      }

      if (Object.keys(summary).length > 0) {
        setCommissionSummary((prev) => ({
          ...prev,
          ...summary,
          available_balance: summary.available_balance ?? summary.total_pending ?? prev.available_balance ?? 0,
          total_withdrawn: summary.total_withdrawn ?? summary.total_paid ?? prev.total_withdrawn ?? 0
        }));
      }

      if (groupBy === 'transactions') {
        const txnParams = new URLSearchParams();
        if (filters.start_date) txnParams.append('start_date', filters.start_date);
        if (filters.end_date) txnParams.append('end_date', filters.end_date);
        if (filters.school_id) txnParams.append('school_id', filters.school_id);
        const txnResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/commissions/transactions?${txnParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (txnResponse.ok) {
          const txnData = await txnResponse.json();
          setCommissionTransactions(txnData.transactions || []);
          if (txnData.available_balance != null) {
            setCommissionSummary((prev) => ({
              ...prev,
              available_balance: txnData.available_balance,
              total_pending: txnData.available_balance
            }));
          }
        }
        return;
      }

      const reportsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/commissions/reports?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (reportsResponse.ok) {
        const reportsData = await reportsResponse.json();
        setCommissionReports(reportsData.reports || []);
      }

      const schoolParams = new URLSearchParams(params);
      schoolParams.set('group_by', 'school');
      const schoolResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/commissions/reports?${schoolParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (schoolResponse.ok) {
        const schoolData = await schoolResponse.json();
        setSchoolWiseReports(schoolData.reports || []);
      }
    } catch (error) {
      console.error('Error loading commission data:', error);
    }
  };

  const getDefaultAgencyCommissionFilters = () => ({
    start_date: '',
    end_date: '',
    school_id: '',
    standard: '',
    division: ''
  });

  const resetAgencyCommissionFilters = async () => {
    const cleared = getDefaultAgencyCommissionFilters();
    setCommissionFilters(cleared);
    if (commissionSubTab === 'withdrawal') {
      await loadCommissionData('transactions', null, cleared);
      return;
    }
    const groupMap = {
      summary: 'detail',
      date: 'date',
      school: 'school',
      division: 'division',
      event: 'event',
      ledger: 'detail'
    };
    await loadCommissionData(groupMap[commissionSubTab] || 'detail', null, cleared);
  };

  const submitAgencyWithdrawal = async () => {
    setWithdrawalMessage('');
    const amount = parseFloat(withdrawalForm.amount);
    const available = Number(commissionSummary.available_balance || 0);
    if (!withdrawalForm.account_number || !withdrawalForm.ifsc_code || !amount) {
      setWithdrawalMessage('Account number, IFSC code and amount are required');
      return;
    }
    if (amount > available) {
      setWithdrawalMessage(`Amount cannot exceed available commission (₹${available.toFixed(2)})`);
      return;
    }

    setWithdrawalLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/commissions/withdrawals`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(withdrawalForm)
      });
      const data = await response.json();
      if (!response.ok) {
        setWithdrawalMessage(data.message || 'Withdrawal failed');
        return;
      }
      setWithdrawalMessage(`Withdrawal of ₹${Number(data.amount).toFixed(2)} submitted successfully`);
      setWithdrawalForm({
        account_holder_name: '',
        account_number: '',
        ifsc_code: '',
        bank_name: '',
        amount: '',
        notes: ''
      });
      await loadCommissionData('transactions');
    } catch (error) {
      console.error('Error submitting agency withdrawal:', error);
      setWithdrawalMessage('Network error while submitting withdrawal');
    } finally {
      setWithdrawalLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'commission') {
      if (commissionSubTab === 'withdrawal') {
        loadCommissionData('transactions');
      } else {
        const groupMap = {
          summary: 'detail',
          date: 'date',
          school: 'school',
          division: 'division',
          event: 'event',
          ledger: 'detail'
        };
        loadCommissionData(groupMap[commissionSubTab] || 'detail');
      }
    }
    if (activeTab === 'reports') {
      loadAgencySalesReport();
    }
  }, [activeTab, commissionSubTab]);

  const loadAgencySalesReport = async (filtersOverride = null) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const filters = filtersOverride || salesFilters;
      const params = new URLSearchParams();
      if (filters.school_id) params.append('school_id', filters.school_id);
      if (filters.assignment_status) params.append('assignment_status', filters.assignment_status);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/devices/sales-report?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSalesReports(data.reports || []);
        setSalesSummary(data.summary || {
          total_devices: 0,
          sold_to_school: 0,
          inventory: 0,
          assigned_to_student: 0
        });
      }
    } catch (error) {
      console.error('Error loading agency sales report:', error);
    }
  };

  const resetAgencySalesFilters = async () => {
    const cleared = { school_id: '', assignment_status: '' };
    setSalesFilters(cleared);
    await loadAgencySalesReport(cleared);
  };

  const handleAddSchool = async (e) => {
    e.preventDefault();
    
    // Validate commission
    const defaultCommission = parseFloat(process.env.REACT_APP_SCHOOL_DEFAULT_COMMISSION) || 10;
    const commissionValue = parseFloat(newSchool.sales_commission);
    
    if (commissionValue > defaultCommission) {
      alert(`Commission cannot exceed ${defaultCommission}%`);
      return;
    }
    
    try {
      // API call to add school
      await api.post('/auth/agency/schools', newSchool);
      setShowAddSchool(false);
      setNewSchool({
        name: '',
        email: '',
        phone: '',
        address: '',
        commission_type: 'percentage',
        sales_commission: process.env.REACT_APP_SCHOOL_DEFAULT_COMMISSION || 10
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error adding school:', error);
      alert(error.message || 'Failed to add school');
    }
  };

  const closeEditSchoolModal = () => {
    setShowEditSchool(false);
    setEditSchoolUsers([]);
    setResetPasswordUser(null);
    setNewUserPassword('');
    setEditSchoolForm({
      id: '',
      name: '',
      email: '',
      phone: '',
      address: '',
      commission_type: 'percentage',
      sales_commission: '',
      status: 'active'
    });
  };

  const handleOpenEditSchool = async (school) => {
    try {
      setEditSchoolLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/agency/schools/${school.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to load school details');
      }

      const schoolData = data.school || school;
      setEditSchoolForm({
        id: schoolData.id,
        name: schoolData.name || '',
        email: schoolData.email || '',
        phone: schoolData.phone || '',
        address: schoolData.address || '',
        commission_type: schoolData.commission_type || 'percentage',
        sales_commission: schoolData.sales_commission ?? '',
        status: schoolData.status || 'active'
      });
      setEditSchoolUsers(Array.isArray(data.login_users) ? data.login_users : []);
      setShowEditSchool(true);
    } catch (error) {
      console.error('Error loading school for edit:', error);
      alert(error.message || 'Unable to open school edit');
    } finally {
      setEditSchoolLoading(false);
    }
  };

  const handleEditSchool = async (e) => {
    e.preventDefault();

    const defaultCommission = parseFloat(process.env.REACT_APP_SCHOOL_DEFAULT_COMMISSION) || 10;
    const commissionValue = parseFloat(editSchoolForm.sales_commission);
    if (commissionValue > defaultCommission) {
      alert(`Commission cannot exceed ${defaultCommission}%`);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/agency/schools/${editSchoolForm.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            name: editSchoolForm.name,
            email: editSchoolForm.email,
            phone: editSchoolForm.phone,
            address: editSchoolForm.address,
            commission_type: editSchoolForm.commission_type,
            sales_commission: editSchoolForm.sales_commission,
            status: editSchoolForm.status
          })
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update school');
      }

      alert(data.message || 'School updated successfully');
      closeEditSchoolModal();
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating school:', error);
      alert(error.message || 'Failed to update school');
    }
  };

  const handleResetSchoolUserPassword = async (e) => {
    e.preventDefault();
    if (!resetPasswordUser || !newUserPassword.trim()) {
      alert('Please enter a new password');
      return;
    }

    try {
      setResettingUserPassword(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/agency/schools/${editSchoolForm.id}/users/${resetPasswordUser.id}/password`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ new_password: newUserPassword.trim() })
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to reset password');
      }

      alert(data.message || 'Password updated successfully');
      setResetPasswordUser(null);
      setNewUserPassword('');
    } catch (error) {
      console.error('Error resetting school user password:', error);
      alert(error.message || 'Failed to reset password');
    } finally {
      setResettingUserPassword(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: btoa(passwordForm.current_password),
          new_password: btoa(passwordForm.new_password)
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert('Password changed successfully');
        setShowChangePassword(false);
        setPasswordForm({
          current_password: '',
          new_password: ''
        });
      } else {
        alert(data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Network error: ' + error.message);
    }
  };

  const closeSingleAssignModal = () => {
    setShowSingleAssign(false);
    setSingleAssignForm({ device_id: '', school_id: '' });
    setSingleAssignDeviceLocked(false);
  };

  const openAssignDeviceModal = (device = null) => {
    if (device) {
      setSingleAssignForm({ device_id: device.device_id, school_id: '' });
      setSingleAssignDeviceLocked(true);
    } else {
      setSingleAssignForm({ device_id: '', school_id: '' });
      setSingleAssignDeviceLocked(false);
    }
    setShowSingleAssign(true);
  };

  const handleSingleAssign = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/devices/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(singleAssignForm)
      });

      const data = await response.json();

      if (response.ok) {
        alert('Device assigned to school successfully');
        closeSingleAssignModal();
        fetchDashboardData();
      } else {
        alert(data.message || 'Failed to assign device');
      }
    } catch (error) {
      console.error('Error assigning device:', error);
      alert('Network error: ' + error.message);
    }
  };

  const handleBulkAssign = async (e) => {
    e.preventDefault();
    
    if (!bulkAssignSchoolId) {
      alert('Please select a school');
      return;
    }

    if (!bulkAssignFile) {
      alert('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', bulkAssignFile);
    formData.append('school_id', bulkAssignSchoolId);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/devices/bulk-assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setBulkAssignResult(data);
        const schoolLabel = data.school_name ? ` to ${data.school_name}` : '';
        alert(
          `Assigned ${data.successful_count} device(s)${schoolLabel}. ` +
          `Failed: ${data.failed_count}. Skipped: ${data.skipped_count || 0}.`
        );
        setShowBulkAssign(false);
        setBulkAssignFile(null);
        setBulkAssignSchoolId('');
        setBulkAssignResult(null);
        fetchDashboardData();
      } else {
        alert(data.message || 'Failed to bulk assign devices');
      }
    } catch (error) {
      console.error('Error bulk assigning devices:', error);
      alert('Network error: ' + error.message);
    }
  };

  const downloadSampleExcel = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/agency/devices/bulk-assign/sample-excel`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'agency_device_ids_sample.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
        return;
      }

      const err = await response.json().catch(() => ({}));
      alert(err.message || 'Could not download sample.');
      return;
    } catch (error) {
      console.error('API sample download failed, using local template:', error);
    }

    const sampleData = [
      { 'Device ID': '861261022297317' },
      { 'Device ID': '86126102229731777' },
      { 'Device ID': '' }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Device IDs');
    XLSX.writeFile(wb, 'agency_device_ids_sample.xlsx');
  };

  const renderOverview = () => (
    <div className="dashboard-overview">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon schools">🏫</div>
          <div className="stat-content">
            <h3>{stats.totalSchools}</h3>
            <p>Total Schools</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon students">👥</div>
          <div className="stat-content">
            <h3>{stats.activeStudents.toLocaleString()}</h3>
            <p>Active Students</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon devices">📱</div>
          <div className="stat-content">
            <h3>{stats.activeDevices.toLocaleString()}</h3>
            <p>Active Devices</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon revenue">💰</div>
          <div className="stat-content">
            <h3>₹{stats.totalRevenue.toLocaleString()}</h3>
            <p>Total Revenue</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon commission">📈</div>
          <div className="stat-content">
            <h3>₹{stats.commissionEarned.toLocaleString()}</h3>
            <p>Commission Earned</p>
          </div>
        </div>
      </div>
      
      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          <div className="activity-item">
            <span className="activity-text">New school registered: ABC School</span>
            <span className="activity-time">2 hours ago</span>
          </div>
          <div className="activity-item">
            <span className="activity-text">Subscription renewed: XYZ Academy</span>
            <span className="activity-time">5 hours ago</span>
          </div>
          <div className="activity-item">
            <span className="activity-text">Payment received: ₹45,000</span>
            <span className="activity-time">1 day ago</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSchools = () => (
    <div className="schools-management">
      <div className="section-header">
        <h3>School Management</h3>
        <button className="btn-primary" onClick={() => setShowAddSchool(true)}>
          + Add New School
        </button>
      </div>
      
      {showAddSchool && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Add New School</h4>
            <form onSubmit={handleAddSchool}>
              <div className="form-grid">
                <div className="agency-field">
                  <label className="field-label">School name</label>
                  <input
                    type="text"
                    placeholder="School name"
                    value={newSchool.name}
                    onChange={(e) => setNewSchool({...newSchool, name: e.target.value})}
                    required
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Email</label>
                  <input
                    type="email"
                    placeholder="Email"
                    value={newSchool.email}
                    onChange={(e) => setNewSchool({...newSchool, email: e.target.value})}
                    required
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Phone</label>
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={newSchool.phone}
                    onChange={(e) => setNewSchool({...newSchool, phone: e.target.value})}
                    required
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Sales commission (%)</label>
                  <input
                    type="number"
                    placeholder="Commission %"
                    value={newSchool.sales_commission}
                    onChange={(e) => setNewSchool({...newSchool, commission_type: 'percentage', sales_commission: e.target.value})}
                    max={process.env.REACT_APP_SCHOOL_DEFAULT_COMMISSION || 10}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>
              <div className="agency-field" style={{ marginBottom: 16 }}>
                <label className="field-label">Address</label>
                <textarea
                  placeholder="Address"
                  value={newSchool.address}
                  onChange={(e) => setNewSchool({...newSchool, address: e.target.value})}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddSchool(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Add School</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showEditSchool && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-wide">
            <h4>Edit School</h4>
            <form onSubmit={handleEditSchool}>
              <div className="form-grid">
                <div className="agency-field">
                  <label className="field-label">School name</label>
                  <input
                    type="text"
                    placeholder="School name"
                    value={editSchoolForm.name}
                    onChange={(e) => setEditSchoolForm({ ...editSchoolForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Email</label>
                  <input
                    type="email"
                    placeholder="School email"
                    value={editSchoolForm.email}
                    onChange={(e) => setEditSchoolForm({ ...editSchoolForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Phone</label>
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={editSchoolForm.phone}
                    onChange={(e) => setEditSchoolForm({ ...editSchoolForm, phone: e.target.value })}
                    required
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Sales commission (%)</label>
                  <input
                    type="number"
                    placeholder="Commission %"
                    value={editSchoolForm.sales_commission}
                    onChange={(e) => setEditSchoolForm({ ...editSchoolForm, commission_type: 'percentage', sales_commission: e.target.value })}
                    max={process.env.REACT_APP_SCHOOL_DEFAULT_COMMISSION || 10}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Status</label>
                  <select
                    value={editSchoolForm.status}
                    onChange={(e) => setEditSchoolForm({ ...editSchoolForm, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="agency-field" style={{ marginBottom: 16 }}>
                <label className="field-label">Address</label>
                <textarea
                  placeholder="Address"
                  value={editSchoolForm.address}
                  onChange={(e) => setEditSchoolForm({ ...editSchoolForm, address: e.target.value })}
                  required
                />
              </div>

              <div className="school-logins-section">
                <h5>School Login Accounts</h5>
                <p className="school-logins-hint">
                  Use these emails to test school portal login. Default password for new schools is <strong>school1234</strong>.
                </p>
                {editSchoolUsers.length === 0 ? (
                  <p className="school-logins-empty">No login users found for this school.</p>
                ) : (
                  <div className="school-logins-table-wrap">
                    <table className="school-logins-table">
                      <thead>
                        <tr>
                          <th>Email (Login)</th>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editSchoolUsers.map((loginUser) => (
                          <tr key={loginUser.id}>
                            <td>{loginUser.email}</td>
                            <td>{loginUser.first_name} {loginUser.last_name}</td>
                            <td>{loginUser.user_type}</td>
                            <td>{loginUser.status}</td>
                            <td>
                              {(loginUser.user_type === 'school' || loginUser.user_type === 'principal') ? (
                                <button
                                  type="button"
                                  className="btn-action btn-assign btn-small"
                                  onClick={() => {
                                    setResetPasswordUser(loginUser);
                                    setNewUserPassword('');
                                  }}
                                >
                                  Change Password
                                </button>
                              ) : (
                                <span className="school-logins-na">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeEditSchoolModal}>Cancel</button>
                <button type="submit" className="btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetPasswordUser && (
        <div className="modal-overlay modal-overlay-nested">
          <div className="modal-content">
            <h4>Change Password</h4>
            <p className="school-logins-hint">
              Set a new password for <strong>{resetPasswordUser.email}</strong>
            </p>
            <form onSubmit={handleResetSchoolUserPassword}>
              <input
                type="password"
                placeholder="New password"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                minLength={4}
                required
                autoComplete="new-password"
              />
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setResetPasswordUser(null);
                    setNewUserPassword('');
                  }}
                  disabled={resettingUserPassword}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={resettingUserPassword}>
                  {resettingUserPassword ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="schools-table">
        <table>
          <thead>
            <tr>
              <th>School Name</th>
              <th>Students</th>
              <th>Status</th>
              <th>Subscription</th>
              <th>Revenue</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {schools.map(school => (
              <tr key={school.id}>
                <td>{school.name}</td>
                <td>{school.students}</td>
                <td><span className="status-badge active">{school.status}</span></td>
                <td>{school.subscription}</td>
                <td>₹{school.revenue.toLocaleString()}</td>
                <td>
                  <button
                    type="button"
                    className="btn-action"
                    onClick={() => handleOpenEditSchool(school)}
                    disabled={editSchoolLoading}
                  >
                    Edit
                  </button>
                  <button className="btn-action danger">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCommission = () => {
    const isGrouped = ['date', 'school', 'division', 'event'].includes(commissionSubTab);
    const rows = commissionSubTab === 'school' ? schoolWiseReports : commissionReports;

    return (
      <div className="reports-section">
        <h3>Commission</h3>
        <p className="page-desc">Switch views from the sidebar Commission menu or the pills below.</p>

        <div className="agency-subtabs">
          {[
            ['summary', 'Summary'],
            ['withdrawal', 'Withdrawal'],
            ['date', 'Date-wise'],
            ['school', 'School-wise'],
            ['division', 'Division-wise'],
            ['event', 'Payment Source'],
            ['ledger', 'Ledger']
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={commissionSubTab === key ? 'active' : ''}
              onClick={() => setCommissionSubTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {commissionSubTab === 'withdrawal' ? (
          <div>
            <div className="summary-cards">
              <div className="summary-card">
                <h4>Available</h4>
                <p className="summary-value">₹{Number(commissionSummary.available_balance || 0).toLocaleString()}</p>
              </div>
              <div className="summary-card">
                <h4>Withdrawn / Paid</h4>
                <p className="summary-value">₹{Number(commissionSummary.total_withdrawn || commissionSummary.total_paid || 0).toLocaleString()}</p>
              </div>
              <div className="summary-card">
                <h4>Total Earned</h4>
                <p className="summary-value">₹{Number(commissionSummary.total_accrued || 0).toLocaleString()}</p>
              </div>
            </div>

            <div className="agency-card-box">
              <h4>Withdrawal request</h4>
              {withdrawalMessage ? (
                <p className={String(withdrawalMessage).toLowerCase().includes('success') ? 'msg-ok' : 'msg-err'}>
                  {withdrawalMessage}
                </p>
              ) : null}
              <div className="agency-field-grid">
                <div className="agency-field">
                  <label className="field-label">Account holder</label>
                  <input
                    placeholder="Account holder name"
                    value={withdrawalForm.account_holder_name}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, account_holder_name: e.target.value })}
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Bank name</label>
                  <input
                    placeholder="Bank name"
                    value={withdrawalForm.bank_name}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, bank_name: e.target.value })}
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Account number *</label>
                  <input
                    placeholder="Account number"
                    value={withdrawalForm.account_number}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, account_number: e.target.value })}
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">IFSC *</label>
                  <input
                    placeholder="IFSC code"
                    value={withdrawalForm.ifsc_code}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, ifsc_code: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Amount *</label>
                  <input
                    type="number"
                    placeholder={`Max ₹${Number(commissionSummary.available_balance || 0).toFixed(2)}`}
                    value={withdrawalForm.amount}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, amount: e.target.value })}
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">Notes</label>
                  <input
                    placeholder="Optional notes"
                    value={withdrawalForm.notes}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, notes: e.target.value })}
                  />
                </div>
              </div>
              <button
                type="button"
                className="btn-primary"
                disabled={withdrawalLoading}
                onClick={submitAgencyWithdrawal}
              >
                {withdrawalLoading ? 'Submitting...' : 'Submit Withdrawal'}
              </button>
            </div>

            <div className="report-filters">
              <div className="agency-field" style={{ flex: '1 1 150px', minWidth: 140 }}>
                <label className="field-label">From</label>
                <input
                  type="date"
                  value={commissionFilters.start_date}
                  onChange={(e) => setCommissionFilters({ ...commissionFilters, start_date: e.target.value })}
                />
              </div>
              <div className="agency-field" style={{ flex: '1 1 150px', minWidth: 140 }}>
                <label className="field-label">To</label>
                <input
                  type="date"
                  value={commissionFilters.end_date}
                  onChange={(e) => setCommissionFilters({ ...commissionFilters, end_date: e.target.value })}
                />
              </div>
              <button type="button" className="btn-primary" onClick={() => loadCommissionData('transactions')}>
                Filter Transactions
              </button>
              <button type="button" className="btn-secondary" onClick={resetAgencyCommissionFilters}>
                Reset
              </button>
            </div>

            <div className="reports-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Details</th>
                    <th>School</th>
                    <th>Requested By</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {commissionTransactions.length === 0 ? (
                    <tr><td colSpan="7">No transactions found</td></tr>
                  ) : (
                    commissionTransactions.map((txn) => (
                      <tr key={`${txn.txn_type}-${txn.id}`}>
                        <td>{new Date(txn.created_at).toLocaleString()}</td>
                        <td>{txn.txn_type === 'withdrawal' ? 'Withdrawal' : 'Commission In'}</td>
                        <td>
                          {txn.txn_type === 'withdrawal'
                            ? `A/C ${txn.account_number || '-'} / IFSC ${txn.ifsc_code || '-'}`
                            : (txn.event_type || '-')}
                        </td>
                        <td>{txn.school_name || '-'}</td>
                        <td>
                          {txn.txn_type === 'withdrawal'
                            ? `${txn.requested_by_name || '-'} ${txn.requested_by_email ? `(${txn.requested_by_email})` : ''}`
                            : '-'}
                        </td>
                        <td style={{ color: txn.txn_type === 'withdrawal' ? '#c62828' : '#2e7d32' }}>
                          {txn.txn_type === 'withdrawal' ? '-' : '+'}₹{Number(txn.amount || 0).toLocaleString()}
                        </td>
                        <td>{txn.status}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <>
        <div className="report-filters">
          <div className="agency-field" style={{ flex: '1 1 140px', minWidth: 130 }}>
            <label className="field-label">From</label>
            <input
              type="date"
              value={commissionFilters.start_date}
              onChange={(e) => setCommissionFilters({ ...commissionFilters, start_date: e.target.value })}
            />
          </div>
          <div className="agency-field" style={{ flex: '1 1 140px', minWidth: 130 }}>
            <label className="field-label">To</label>
            <input
              type="date"
              value={commissionFilters.end_date}
              onChange={(e) => setCommissionFilters({ ...commissionFilters, end_date: e.target.value })}
            />
          </div>
          <div className="agency-field" style={{ flex: '1 1 160px', minWidth: 150 }}>
            <label className="field-label">School</label>
            <select
              className="filter-select"
              value={commissionFilters.school_id}
              onChange={(e) => setCommissionFilters({ ...commissionFilters, school_id: e.target.value })}
            >
              <option value="">All Schools</option>
              {schools.map((school) => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
          </div>
          <div className="agency-field" style={{ flex: '1 1 110px', minWidth: 100 }}>
            <label className="field-label">Standard</label>
            <input
              placeholder="e.g. 5th"
              value={commissionFilters.standard}
              onChange={(e) => setCommissionFilters({ ...commissionFilters, standard: e.target.value })}
            />
          </div>
          <div className="agency-field" style={{ flex: '1 1 110px', minWidth: 100 }}>
            <label className="field-label">Division</label>
            <input
              placeholder="e.g. A"
              value={commissionFilters.division}
              onChange={(e) => setCommissionFilters({ ...commissionFilters, division: e.target.value })}
            />
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              const groupMap = {
                summary: 'detail',
                date: 'date',
                school: 'school',
                division: 'division',
                event: 'event',
                ledger: 'detail'
              };
              loadCommissionData(groupMap[commissionSubTab] || 'detail');
            }}
          >
            Apply Filters
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={resetAgencyCommissionFilters}
          >
            Reset
          </button>
        </div>

        <div className="report-summary">
          <div className="summary-cards">
            <div className="summary-card">
              <h4>Total Commission</h4>
              <p className="summary-value">₹{Number(commissionSummary.total_accrued || 0).toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h4>Pending</h4>
              <p className="summary-value">₹{Number(commissionSummary.total_pending || 0).toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h4>Paid</h4>
              <p className="summary-value">₹{Number(commissionSummary.total_paid || 0).toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h4>Agency Rate</h4>
              <p className="summary-value">{formatCommissionRate(commissionSummary, stats)}</p>
            </div>
          </div>
        </div>

        <div className="reports-table">
          <table>
            <thead>
              <tr>
                {isGrouped ? (
                  <>
                    <th>Group</th>
                    <th>Entries</th>
                    <th>Base Amount</th>
                    <th>Commission</th>
                  </>
                ) : (
                  <>
                    <th>Date</th>
                    <th>Event</th>
                    <th>School</th>
                    <th>Device</th>
                    <th>Class/Div</th>
                    <th>Commission</th>
                    <th>Status</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={isGrouped ? 4 : 7}>No commission records yet.</td>
                </tr>
              ) : isGrouped ? (
                rows.map((row, index) => (
                  <tr key={index}>
                    <td>{row.report_label || row.report_key}</td>
                    <td>{row.entry_count}</td>
                    <td>₹{Number(row.total_base_amount || 0).toLocaleString()}</td>
                    <td>₹{Number(row.total_commission || 0).toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>{new Date(row.created_at).toLocaleDateString()}</td>
                    <td>{row.event_type}</td>
                    <td>{row.school_name || '-'}</td>
                    <td>{row.device_id || '-'}</td>
                    <td>{row.standard || '-'} / {row.division || '-'}</td>
                    <td>₹{Number(row.commission_amount || 0).toLocaleString()}</td>
                    <td>{row.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
          </>
        )}
      </div>
    );
  };

  const renderReports = () => (
    <div className="reports-section">
      <h3>Device Sales Report</h3>
      <p className="page-desc">
        Devices sold/assigned from this agency to schools (and student if already assigned).
      </p>

      <div className="summary-cards">
        <div className="summary-card">
          <h4>Total Devices</h4>
          <p className="summary-value">{salesSummary.total_devices || 0}</p>
        </div>
        <div className="summary-card">
          <h4>Sold to School</h4>
          <p className="summary-value">{salesSummary.sold_to_school || 0}</p>
        </div>
        <div className="summary-card">
          <h4>Inventory</h4>
          <p className="summary-value">{salesSummary.inventory || 0}</p>
        </div>
        <div className="summary-card">
          <h4>With Student</h4>
          <p className="summary-value">{salesSummary.assigned_to_student || 0}</p>
        </div>
      </div>

      <div className="report-filters">
        <div className="agency-field" style={{ flex: '1 1 180px', minWidth: 160 }}>
          <label className="field-label">School</label>
          <select
            className="filter-select"
            value={salesFilters.school_id}
            onChange={(e) => setSalesFilters({ ...salesFilters, school_id: e.target.value })}
          >
            <option value="">All Schools</option>
            {schools.map((school) => (
              <option key={school.id} value={school.id}>{school.name}</option>
            ))}
          </select>
        </div>
        <div className="agency-field" style={{ flex: '1 1 180px', minWidth: 160 }}>
          <label className="field-label">Status</label>
          <select
            className="filter-select"
            value={salesFilters.assignment_status}
            onChange={(e) => setSalesFilters({ ...salesFilters, assignment_status: e.target.value })}
          >
            <option value="">All Status</option>
            <option value="assigned">Sold to School</option>
            <option value="inventory">Inventory Only</option>
          </select>
        </div>
        <button type="button" className="btn-primary" onClick={() => loadAgencySalesReport()}>Apply Filters</button>
        <button type="button" className="btn-secondary" onClick={resetAgencySalesFilters}>Reset</button>
      </div>

      <div className="reports-table">
        <table>
          <thead>
            <tr>
              <th>Device ID</th>
              <th>Type / Model</th>
              <th>School</th>
              <th>Student</th>
              <th>Class/Div</th>
              <th>Sale Status</th>
              <th>Device Status</th>
              <th>Added On</th>
            </tr>
          </thead>
          <tbody>
            {salesReports.length === 0 ? (
              <tr><td colSpan="8">No device sales records found</td></tr>
            ) : (
              salesReports.map((row) => (
                <tr key={row.id || row.device_id}>
                  <td>{row.device_id}</td>
                  <td>{row.type || '-'}{row.model ? ` / ${row.model}` : ''}</td>
                  <td>{row.school_name || '-'}</td>
                  <td>{(row.student_name || '').trim() || '-'}</td>
                  <td>{row.standard || '-'} / {row.division || '-'}</td>
                  <td>{row.sale_status === 'sold_to_school' ? 'Sold to School' : 'Inventory'}</td>
                  <td>{row.status || '-'}</td>
                  <td>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDevices = () => (
    <div className="devices-management">
      <div className="section-header">
        <h3>Device Management</h3>
        <div className="header-buttons">
          <button className="btn-primary" onClick={() => openAssignDeviceModal()}>
            Assign Single Device
          </button>
          <button className="btn-secondary" onClick={() => setShowBulkAssign(true)}>
            Bulk Assign Devices
          </button>
        </div>
      </div>
      
      {showSingleAssign && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Assign Device to School</h4>
            <form onSubmit={handleSingleAssign}>
              <div className="form-grid">
                <div className="agency-field">
                  <label className="field-label">Device ID</label>
                  <input
                    type="text"
                    placeholder="Device ID"
                    value={singleAssignForm.device_id}
                    onChange={(e) => setSingleAssignForm({...singleAssignForm, device_id: e.target.value})}
                    readOnly={singleAssignDeviceLocked}
                    required
                  />
                </div>
                <div className="agency-field">
                  <label className="field-label">School</label>
                  <select
                    value={singleAssignForm.school_id}
                    onChange={(e) => setSingleAssignForm({...singleAssignForm, school_id: e.target.value})}
                    required
                  >
                    <option value="">Select School</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>{school.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeSingleAssignModal}>Cancel</button>
                <button type="submit" className="btn-primary">Assign Device</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showBulkAssign && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Bulk Assign Devices to School</h4>
            <form onSubmit={handleBulkAssign}>
              <div className="form-group">
                <label>Select School</label>
                <select
                  value={bulkAssignSchoolId}
                  onChange={(e) => setBulkAssignSchoolId(e.target.value)}
                  required
                >
                  <option value="">Choose school for this upload</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>{school.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Upload Excel File (Device ID column only)</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setBulkAssignFile(e.target.files[0])}
                  required
                />
              </div>
              <div className="form-group">
                <p style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>
                  Select the school first, then upload Excel with <strong>Device ID</strong> only.
                  A device already assigned to another school will be rejected.
                </p>
                <button type="button" onClick={downloadSampleExcel} className="btn-link">
                  Download Sample Excel
                </button>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => { setShowBulkAssign(false); setBulkAssignSchoolId(''); setBulkAssignFile(null); }}>Cancel</button>
                <button type="submit" className="btn-primary">Bulk Assign</button>
              </div>
            </form>
            {bulkAssignResult && (
              <div className="bulk-result">
                <p>Successfully assigned: {bulkAssignResult.successful_count}</p>
                <p>Failed: {bulkAssignResult.failed_count}</p>
                {bulkAssignResult.errors.length > 0 && (
                  <div className="error-list">
                    {bulkAssignResult.errors.map((error, index) => (
                      <p key={index}>Row {error.row}: {error.message}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="devices-table">
        <table>
          <thead>
            <tr>
              <th>Device ID</th>
              <th>Type</th>
              <th>Status</th>
              <th>School</th>
              <th>Battery</th>
              <th>Last Update</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.map(device => (
              <tr key={device.id}>
                <td>{device.device_id}</td>
                <td>{device.type}</td>
                <td><span className={`status-badge ${device.status}`}>{device.status}</span></td>
                <td>{device.school_name || 'Unassigned'}</td>
                <td>{device.battery_level}%</td>
                <td>{new Date(device.last_update).toLocaleString()}</td>
                <td>
                  {device.school_id ? (
                    <span className="assigned-label" title={device.school_name || 'Assigned to school'}>
                      Assigned
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn-action btn-assign"
                      onClick={() => openAssignDeviceModal(device)}
                    >
                      Assign to School
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className={`agency-app ${mobileNavOpen ? 'nav-open' : ''}`}>
      {mobileNavOpen && <div className="agency-backdrop" onClick={closeMobileNav} aria-hidden="true" />}

      <aside className="agency-sidebar">
        <div className="agency-brand">
          <h1>Track My Child</h1>
          <p>Agency Portal</p>
        </div>

        <nav className="agency-nav" aria-label="Agency menu">
          <button
            type="button"
            className={`agency-nav-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => goToTab('overview')}
          >
            Overview
          </button>

          <button
            type="button"
            className={`agency-nav-item ${activeTab === 'schools' ? 'active' : ''}`}
            onClick={() => goToTab('schools')}
          >
            Schools
          </button>

          <button
            type="button"
            className={`agency-nav-parent ${expandedMenus.devices || activeTab === 'devices' ? 'open' : ''}`}
            onClick={() => toggleMenu('devices')}
          >
            <span>Devices</span>
            <span className="agency-nav-caret">▼</span>
          </button>
          {expandedMenus.devices && (
            <div className="agency-nav-sub">
              <button
                type="button"
                className={`agency-nav-subitem ${activeTab === 'devices' ? 'active' : ''}`}
                onClick={() => goToTab('devices', { expand: 'devices' })}
              >
                Inventory
              </button>
              <button
                type="button"
                className="agency-nav-subitem"
                onClick={() => {
                  goToTab('devices', { expand: 'devices' });
                  openAssignDeviceModal();
                }}
              >
                Assign Device
              </button>
              <button
                type="button"
                className="agency-nav-subitem"
                onClick={() => {
                  goToTab('devices', { expand: 'devices' });
                  setShowBulkAssign(true);
                }}
              >
                Bulk Assign
              </button>
            </div>
          )}

          <button
            type="button"
            className={`agency-nav-parent ${expandedMenus.commission || activeTab === 'commission' ? 'open' : ''}`}
            onClick={openCommissionMenu}
          >
            <span>Commission</span>
            <span className="agency-nav-caret">▼</span>
          </button>
          {expandedMenus.commission && (
            <div className="agency-nav-sub">
              {[
                ['summary', 'Summary'],
                ['withdrawal', 'Withdrawal'],
                ['date', 'Date-wise'],
                ['school', 'School-wise'],
                ['division', 'Division-wise'],
                ['event', 'Payment Source'],
                ['ledger', 'Detailed Ledger']
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`agency-nav-subitem ${activeTab === 'commission' && commissionSubTab === key ? 'active' : ''}`}
                  onClick={() => goToTab('commission', { submenu: key })}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            className={`agency-nav-item ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => goToTab('reports')}
          >
            Sales Reports
          </button>
        </nav>

        <div className="agency-sidebar-footer">
          <button type="button" onClick={() => { setShowChangePassword(true); closeMobileNav(); }}>
            Change Password
          </button>
          <button type="button" className="logout" onClick={onLogout}>
            Logout
          </button>
        </div>
      </aside>

      <div className="agency-main">
        <header className="agency-topbar">
          <div className="agency-topbar-left">
            <button
              type="button"
              className="agency-menu-toggle"
              onClick={() => setMobileNavOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              Menu
            </button>
            <div>
              <h2>{pageTitles[activeTab]?.title || 'Agency'}</h2>
              <p>
                {pageTitles[activeTab]?.subtitle}
                {user?.name || user?.email ? ` · ${user?.name || user?.email}` : ''}
              </p>
            </div>
          </div>
        </header>

        {showChangePassword && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h4>Change Password</h4>
              <p>Update your agency login password.</p>
              <form onSubmit={handleChangePassword}>
                <div className="agency-field-grid">
                  <div className="agency-field full">
                    <label className="field-label">Current password</label>
                    <input
                      type="password"
                      placeholder="Current password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                      required
                    />
                  </div>
                  <div className="agency-field full">
                    <label className="field-label">New password</label>
                    <input
                      type="password"
                      placeholder="New password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowChangePassword(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">Change Password</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="agency-content">
          <div className="agency-panel">
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'schools' && renderSchools()}
            {activeTab === 'devices' && renderDevices()}
            {activeTab === 'commission' && renderCommission()}
            {activeTab === 'reports' && renderReports()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyDashboard;
